import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Save, Plus, Trash2, X, MapPin, Percent } from "lucide-react";

const VT_DEFAULTS = [
    "Auto", "Mini Cab", "Pocket Rocket", "Premium Cab",
    "Yugo Bizz", "Rental", "Cab XL",
];

const Field = ({ label, value, onChange, testid, step = "0.01" }) => (
    <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
        <input
            type="number"
            step={step}
            value={value}
            onChange={onChange}
            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500/30"
            data-testid={testid}
        />
    </div>
);

const FareRules = () => {
    const [cities, setCities] = useState([]);
    const [activeCity, setActiveCity] = useState("");
    const [rules, setRules] = useState([]);
    const [editing, setEditing] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [showCityForm, setShowCityForm] = useState(false);
    const [showVtForm, setShowVtForm] = useState(false);
    const [newCity, setNewCity] = useState("");
    const [newVt, setNewVt] = useState({
        vehicle_type: "",
        base_fare: 50,
        per_km: 12,
        per_min: 1.5,
        min_fare: 60,
        surge: 1.0,
        commission_pct: 3,
    });
    const [err, setErr] = useState("");

    const loadCities = async () => {
        const c = await api.get("/fare-rules/cities");
        setCities(c.data);
        if (c.data.length > 0 && !activeCity) setActiveCity(c.data[0]);
    };

    const loadRules = async (city) => {
        if (!city) return;
        const r = await api.get(`/fare-rules?city=${encodeURIComponent(city)}`);
        setRules(r.data);
        const e = {};
        r.data.forEach((rule) => { e[rule.id] = { ...rule }; });
        setEditing(e);
    };

    useEffect(() => { loadCities(); }, []);
    useEffect(() => { if (activeCity) loadRules(activeCity); }, [activeCity]);

    const save = async (rule) => {
        setSavingId(rule.id);
        setErr("");
        try {
            const e = editing[rule.id];
            await api.put(`/fare-rules/${rule.id}`, {
                city: rule.city,
                vehicle_type: rule.vehicle_type,
                base_fare: parseFloat(e.base_fare),
                per_km: parseFloat(e.per_km),
                per_min: parseFloat(e.per_min),
                min_fare: parseFloat(e.min_fare),
                surge: parseFloat(e.surge || 1),
                commission_pct: parseFloat(e.commission_pct || 18),
            });
            await loadRules(activeCity);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSavingId(null);
        }
    };

    const removeRule = async (rule) => {
        if (!window.confirm(`Remove ${rule.vehicle_type} for ${rule.city}?`)) return;
        await api.delete(`/fare-rules/${rule.id}`);
        loadRules(activeCity);
    };

    const addCity = async () => {
        setErr("");
        try {
            await api.post("/fare-rules/cities", { name: newCity.trim() });
            setNewCity("");
            setShowCityForm(false);
            await loadCities();
            setActiveCity(newCity.trim());
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const addVehicleType = async () => {
        setErr("");
        try {
            await api.post("/fare-rules", {
                city: activeCity,
                vehicle_type: newVt.vehicle_type,
                base_fare: parseFloat(newVt.base_fare),
                per_km: parseFloat(newVt.per_km),
                per_min: parseFloat(newVt.per_min),
                min_fare: parseFloat(newVt.min_fare),
                surge: parseFloat(newVt.surge),
                commission_pct: parseFloat(newVt.commission_pct),
            });
            setShowVtForm(false);
            setNewVt({ ...newVt, vehicle_type: "" });
            loadRules(activeCity);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const update = (id, key, val) => {
        setEditing((e) => ({ ...e, [id]: { ...e[id], [key]: val } }));
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Fare Rules"
                subtitle="Pricing per city × vehicle type. Set commission % per row."
                action={
                    <button
                        onClick={() => setShowCityForm(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold"
                        data-testid="add-city-btn"
                    >
                        <Plus className="w-4 h-4" /> Launch new city
                    </button>
                }
            />

            {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{err}</div>}

            {/* City tabs */}
            <div className="bg-white border border-slate-200 rounded-2xl p-2 flex flex-wrap gap-1">
                {cities.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-slate-400">No cities yet — launch your first city.</div>
                ) : cities.map((c) => (
                    <button
                        key={c}
                        onClick={() => setActiveCity(c)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                            activeCity === c
                                ? "bg-mint-500 text-white"
                                : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid={`city-tab-${c}`}
                    >
                        <MapPin className="w-3.5 h-3.5" /> {c}
                    </button>
                ))}
            </div>

            {activeCity && (
                <div className="flex justify-end">
                    <button
                        onClick={() => setShowVtForm(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-navy-900 text-sm font-semibold"
                        data-testid="add-vehicle-btn"
                    >
                        <Plus className="w-4 h-4" /> Add vehicle type for {activeCity}
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {rules.map((r) => {
                    const e = editing[r.id] || r;
                    const sample = (e.base_fare || 0) + (e.per_km || 0) * 5 + (e.per_min || 0) * 15;
                    return (
                        <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`fare-card-${r.vehicle_type}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="font-display font-bold text-navy-900 text-lg">{r.vehicle_type}</div>
                                    <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{r.city}</div>
                                </div>
                                <button onClick={() => removeRule(r)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg" data-testid={`fare-delete-${r.vehicle_type}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="text-xs text-slate-500 mb-4">Sample 5km/15min ≈ <span className="font-semibold text-mint-700">{formatCurrency(sample)}</span></div>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <Field label="Base fare (₹)" value={e.base_fare} onChange={(ev) => update(r.id, "base_fare", ev.target.value)} testid={`fare-base-${r.vehicle_type}`} />
                                <Field label="Min fare (₹)" value={e.min_fare} onChange={(ev) => update(r.id, "min_fare", ev.target.value)} testid={`fare-min-${r.vehicle_type}`} />
                                <Field label="Per km (₹)" value={e.per_km} onChange={(ev) => update(r.id, "per_km", ev.target.value)} testid={`fare-perkm-${r.vehicle_type}`} />
                                <Field label="Per min (₹)" value={e.per_min} onChange={(ev) => update(r.id, "per_min", ev.target.value)} testid={`fare-permin-${r.vehicle_type}`} />
                                <Field label="Surge ×" value={e.surge || 1} onChange={(ev) => update(r.id, "surge", ev.target.value)} testid={`fare-surge-${r.vehicle_type}`} />
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Percent className="w-3 h-3" /> Commission %</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        value={e.commission_pct || 18}
                                        onChange={(ev) => update(r.id, "commission_pct", ev.target.value)}
                                        className="w-full px-3 py-2 text-sm bg-mint-50 border border-mint-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500/30 font-semibold"
                                        data-testid={`fare-commission-${r.vehicle_type}`}
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => save(r)}
                                disabled={savingId === r.id}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold disabled:opacity-60"
                                data-testid={`fare-save-${r.vehicle_type}`}
                            >
                                <Save className="w-4 h-4" />
                                {savingId === r.id ? "Saving…" : "Save"}
                            </button>                        </div>
                    );
                })}
            </div>

            {/* Add City Modal */}
            {showCityForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowCityForm(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="add-city-form">
                        <button onClick={() => setShowCityForm(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                        <h3 className="font-display font-bold text-xl text-navy-900 mb-2">Launch in a new city</h3>
                        <p className="text-sm text-slate-500 mb-4">We'll pre-fill the standard 7 vehicle types — you can tweak each card afterwards.</p>
                        <input
                            value={newCity}
                            onChange={(e) => setNewCity(e.target.value)}
                            placeholder="e.g. Lucknow, Ranchi, Indore"
                            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg mb-4"
                            data-testid="new-city-input"
                        />
                        <button onClick={addCity} className="w-full py-2.5 rounded-full bg-mint-500 hover:bg-mint-600 text-white font-semibold text-sm" data-testid="new-city-submit">
                            Launch city
                        </button>
                    </div>
                </div>
            )}

            {/* Add Vehicle Type Modal */}
            {showVtForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowVtForm(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="add-vt-form">
                        <button onClick={() => setShowVtForm(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                        <h3 className="font-display font-bold text-xl text-navy-900 mb-1">Add vehicle type</h3>
                        <p className="text-sm text-slate-500 mb-4">City: <span className="font-semibold text-mint-700">{activeCity}</span></p>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Vehicle type</label>
                                <input
                                    list="vt-options"
                                    value={newVt.vehicle_type}
                                    onChange={(e) => setNewVt({ ...newVt, vehicle_type: e.target.value })}
                                    className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg"
                                    placeholder="Pocket Rocket / EV / etc."
                                    data-testid="new-vt-name"
                                />
                                <datalist id="vt-options">
                                    {VT_DEFAULTS.map((v) => <option key={v} value={v} />)}
                                </datalist>
                            </div>
                            <Field label="Base fare (₹)" value={newVt.base_fare} onChange={(e) => setNewVt({ ...newVt, base_fare: e.target.value })} testid="new-vt-base" />
                            <Field label="Min fare (₹)" value={newVt.min_fare} onChange={(e) => setNewVt({ ...newVt, min_fare: e.target.value })} testid="new-vt-min" />
                            <Field label="Per km (₹)" value={newVt.per_km} onChange={(e) => setNewVt({ ...newVt, per_km: e.target.value })} testid="new-vt-perkm" />
                            <Field label="Per min (₹)" value={newVt.per_min} onChange={(e) => setNewVt({ ...newVt, per_min: e.target.value })} testid="new-vt-permin" />
                            <Field label="Surge ×" value={newVt.surge} onChange={(e) => setNewVt({ ...newVt, surge: e.target.value })} testid="new-vt-surge" />
                            <Field label="Commission %" value={newVt.commission_pct} onChange={(e) => setNewVt({ ...newVt, commission_pct: e.target.value })} testid="new-vt-commission" />
                        </div>
                        <button onClick={addVehicleType} className="mt-4 w-full py-2.5 rounded-full bg-mint-500 hover:bg-mint-600 text-white font-semibold text-sm" data-testid="new-vt-submit">
                            Add vehicle
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FareRules;
